import { observable, computed, action, runInAction } from 'mobx';
import autobind from 'autobind-decorator';
import { asyncAction } from 'mobx-utils';
import { ScoreService } from 'services';
import { ScoreUtil } from 'common/utils/scoreUtil';
import WebSocketUtil from 'common/utils/socketUtil';
import SoccerModel from 'routes/score/soccer/store/model';
import BaseballModel from 'routes/score/baseball/store/model';
import BasketballModel from 'routes/score/basketball/store/model';
import VolleyballModel from 'routes/score/volleyball/store/model';
import HockeyModel from 'routes/score/hockey/store/model';
import FootballModel from 'routes/score/football/store/model';
import {
  getFilterList,
  getDeletedGames,
  getConcatList,
  setDefaultList,
  getFilterDeleteFinalList
} from './util';
import { _SPORTS_WS_TOPIC } from 'common/constants/sportsConstants';
import AbstractScoreCommon from '../../store/common';

@autobind
class MajorStore extends AbstractScoreCommon {
  socket = null;
  isSub = false;

  // DEFAULT_VIEW, SPORTS_TYPE_VIEW, TIME_SORT_VIEW
  @observable
  viewType = 'TIME_SORT_VIEW';

  @observable
  sportsView = {
    soccer: [],
    baseball: [],
    basketball: [],
    volleyball: [],
    hockey: [],
    football: []
  };

  @observable
  loading = false;

  @observable
  selectedTab = 'ALL';

  @observable
  selectedDate = '';

  constructor() {
    super();

    this.localstorage.getAsyncStringItem('majorViewType').then((success) => {
      if (!!success) {
        this.viewType = success;
      } else {
        this.localstorage.setItem('majorViewType', 'TIME_SORT_VIEW');
      }
    });
  }

  connectSocket(topic, type = null) {
    this.socket = new WebSocketUtil(topic);

    this.socket.ws.onmessage = (event) => {
      if (event) {
        let data = event.data;

        try {
          if (data === 'OK') {
            this.isSub = true;
          } else {
            let messageData = JSON.parse(data);

            if (messageData) {
              const { TOPIC, MESSAGE } = messageData;

              if (TOPIC.includes('sports')) {
                const data = JSON.parse(MESSAGE);
                const { sportsView } = this;
                const sportsTypeKeyArr = Object.keys(sportsView);
                let index = -1;

                sportsTypeKeyArr.forEach((key) => {
                  if (TOPIC.includes('cheers')) {
                    if (data.hasOwnProperty('game_id')) {
                      index = sportsView[key].findIndex(
                        (item) => item.id === parseInt(data.game_id)
                      );
                    }
                    if (Boolean(~index)) {
                      sportsView[key][index].cheerCount++;
                    }
                  } else {
                    if (data.hasOwnProperty('gameId')) {
                      index = sportsView[key].findIndex(
                        (item) => item.id === data.gameId
                      );
                    }

                    if (TOPIC.includes(key)) {
                      [
                        ..._SPORTS_WS_TOPIC['soccer'],
                        ..._SPORTS_WS_TOPIC['baseball'],
                        ..._SPORTS_WS_TOPIC['basketball'],
                        ..._SPORTS_WS_TOPIC['volleyball'],
                        ..._SPORTS_WS_TOPIC['hockey']
                      ].forEach((item) => {
                        if (TOPIC === `${item.topic}`) {
                          if (Boolean(~index)) {
                            const check = this.selectedList.length > 0;
                            const checkFavorite = this.selectedList.some(
                              (item) => item.id === data.gameId
                            );

                            if (TOPIC.includes('games')) {
                              if (
                                key === 'basketball' ||
                                key === 'volleyball'
                              ) {
                                sportsView[key][index][item.funcName](
                                  data[item.key],
                                  check,
                                  checkFavorite
                                );
                              } else {
                                sportsView[key][index][item.funcName](
                                  data[item.key]
                                );
                              }
                            } else if (TOPIC.includes('period-data')) {
                              if (
                                key === 'basketball' ||
                                key === 'volleyball'
                              ) {
                                sportsView[key][index][item.funcName](
                                  data[item.key]
                                );
                              } else {
                                sportsView[key][index][item.funcName](
                                  data[item.key],
                                  check,
                                  checkFavorite
                                );
                              }
                            } else {
                              sportsView[key][index][item.funcName](
                                data[item.key]
                              );
                            }
                          }
                        }
                      });
                    }
                  }
                });
              }
            }
          }
        } catch (error) {
          console.log('=== socket on message error ===', error);
        }
      }
    };
  }

  /**********************************************************
   * computed
   **********************************************************/
  @computed
  get sportsViewObject() {
    const { sportsView, selectedTab, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    const filterList = getFilterList(selectedTab, sportsTypeKeyArr, sportsView);
    const deleteSelectedGames = getDeletedGames(
      sportsTypeKeyArr,
      filterList,
      selectedGames
    );

    return deleteSelectedGames;
  }

  @computed
  get sportsTimeViewList() {
    const { sportsView, selectedTab, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    const filterList = getFilterDeleteFinalList(
      selectedTab,
      sportsTypeKeyArr,
      sportsView
    );
    const deleteSelectedGames = getDeletedGames(
      sportsTypeKeyArr,
      filterList,
      selectedGames
    );
    const list = getConcatList(deleteSelectedGames);

    return list;
  }

  @computed
  get sportsTimeViewFinalList() {
    const { sportsView, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    const filterList = getFilterList('FINAL', sportsTypeKeyArr, sportsView);
    const deleteSelectedGames = getDeletedGames(
      sportsTypeKeyArr,
      filterList,
      selectedGames
    );
    const list = getConcatList(deleteSelectedGames);

    return list;
  }

  @computed
  get sportsDefaultList() {
    const { sportsView, selectedTab, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    const filterList = getFilterDeleteFinalList(
      selectedTab,
      sportsTypeKeyArr,
      sportsView
    );
    const deleteSelectedGames = getDeletedGames(
      sportsTypeKeyArr,
      filterList,
      selectedGames
    );
    const list = getConcatList(deleteSelectedGames);

    return setDefaultList(list);
  }

  @computed
  get sportsDefaultFinalList() {
    const { sportsView, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    const filterList = getFilterList('FINAL', sportsTypeKeyArr, sportsView);
    const deleteSelectedGames = getDeletedGames(
      sportsTypeKeyArr,
      filterList,
      selectedGames
    );
    const list = getConcatList(deleteSelectedGames);

    return setDefaultList(list);
  }

  @computed
  get selectedList() {
    const { sportsView, selectedGames } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    let selectedGameList = {
      soccer: [],
      baseball: [],
      basketball: [],
      volleyball: [],
      hockey: [],
      football: []
    };

    if (this.selectedGames.length > 0) {
      sportsTypeKeyArr.forEach((key) => {
        const filterList = sportsView[key].filter((item) => {
          const checkList = selectedGames.filter((f) => f.sportsType === key);

          if (checkList.some((id) => item.id === id.key)) {
            return item;
          }
        });

        if (filterList.length > 0) {
          selectedGameList[key] = [...filterList];
        }
      });
    }

    return ScoreUtil.sortSportsList([
      ...selectedGameList.soccer,
      ...selectedGameList.baseball,
      ...selectedGameList.basketball,
      ...selectedGameList.volleyball,
      ...selectedGameList.hockey,
      ...selectedGameList.football
    ]);
  }

  @computed
  get majorCount() {
    const { sportsView } = this;
    const sportsTypeKeyArr = Object.keys(sportsView);
    let result = {
      all: 0,
      ready: 0,
      inProgress: 0,
      final: 0
    };

    sportsTypeKeyArr.forEach((key) => {
      result.all += sportsView[key].length;
      const count = ScoreUtil.getSportsStatusCount(sportsView[key]);

      result.ready += count.ready;
      result.inProgress += count.inProgress;
      result.final += count.final;
    });

    return result;
  }

  /**********************************************************
   * actions
   **********************************************************/
  @action
  closeSocket() {
    this.socket.close(() => {
      runInAction(() => {
        this.isSub = false;
        this.socket = null;
      });
    });
  }

  @action
  setMajorViewType(type) {
    const { setItem } = this.localstorage;

    setItem('majorViewType', type);

    this.viewType = type;
  }

  @action
  setMajorSelectedTab(tab) {
    this.selectedTab = tab;
  }

  /**********************************************************
   * async actions
   **********************************************************/
  @asyncAction
  *getMajor(date) {
    this.loading = true;
    this.selectedDate = date;

    try {
      // TODO: 웹소켓;
      if (!this.isSub) {
        yield this.connectSocket(
          [
            ..._SPORTS_WS_TOPIC['soccer'],
            ..._SPORTS_WS_TOPIC['baseball'],
            ..._SPORTS_WS_TOPIC['basketball'],
            ..._SPORTS_WS_TOPIC['volleyball'],
            ..._SPORTS_WS_TOPIC['hockey']
          ].map((item) => item.topic)
          // _SPORTS_WS_TOPIC['major'].map((item) => item.topic)
        );
      }

      const result = yield ScoreService.getPopularGames(date);

      if (result) {
        let copySportsView = { ...this.sportsView };

        Object.keys(result).forEach((key) => {
          if (key !== 'tennis') {
            copySportsView[key] = result[key].map((item) => {
              if (key === 'soccer') {
                return new SoccerModel(item);
              } else if (key === 'baseball') {
                return new BaseballModel(item);
              } else if (key === 'basketball') {
                return new BasketballModel(item);
              } else if (key === 'volleyball') {
                return new VolleyballModel(item);
              } else if (key === 'hockey') {
                return new HockeyModel(item);
              } else if (key === 'football') {
                return new FootballModel(item);
              }
            });
          }
        });

        this.sportsView = copySportsView;
        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export default MajorStore;
